import { useState, useRef, useEffect } from 'react';

const agents = [
    {
        id: 'scout',
        name: 'Data Scout',
        emoji: '🔍',
        color: 'cyan',
        description: 'Discovers & recommends datasets',
        stages: ['data']
    },
    {
        id: 'engineer',
        name: 'Data Engineer',
        emoji: '🧹',
        color: 'green',
        description: 'Cleans & preprocesses data',
        stages: ['engineer']
    },
    {
        id: 'ml_engineer',
        name: 'ML Engineer',
        emoji: '🏗️',
        color: 'orange',
        description: 'Trains & tunes models',
        stages: ['train']
    },
    {
        id: 'evaluator',
        name: 'Evaluator',
        emoji: '📊',
        color: 'purple',
        description: 'Analyzes metrics & results',
        stages: ['train', 'eval']
    },
    {
        id: 'mlops',
        name: 'MLOps',
        emoji: '🚀',
        color: 'red',
        description: 'Deploys & monitors models',
        stages: ['deploy']
    },
    {
        id: 'mentor',
        name: 'Mentor',
        emoji: '👨‍🏫',
        color: 'blue',
        description: 'Teaches & guides',
        stages: ['data', 'engineer', 'train', 'eval', 'deploy']
    }
];

function AgentSidebar({
    activeAgent,
    onAgentChange,
    chatHistory,
    onSendMessage,
    isProcessing,
    isTyping,
    currentStage,
    pipelineStatus,
    onCloseSidebar
}) {
    const [message, setMessage] = useState('');
    const [isExpanded, setIsExpanded] = useState(true);
    const [selectedAgent, setSelectedAgent] = useState(activeAgent);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        scrollToBottom();
    }, [chatHistory, isTyping]);

    useEffect(() => {
        setSelectedAgent(activeAgent);
    }, [activeAgent]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSend = () => {
        if (message.trim() && !isProcessing) {
            onSendMessage(message, selectedAgent);
            setMessage('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const getAgentColorClass = (color) => {
        const colors = {
            cyan: 'border-cyan-500 bg-cyan-50 text-cyan-700',
            green: 'border-green-500 bg-green-50 text-green-700',
            orange: 'border-orange-500 bg-orange-50 text-orange-700',
            purple: 'border-purple-500 bg-purple-50 text-purple-700',
            red: 'border-red-500 bg-red-50 text-red-700',
            blue: 'border-blue-500 bg-blue-50 text-blue-700'
        };
        return colors[color] || colors.blue;
    };

    const isAgentActive = (agent) => {
        return agent.stages.includes(currentStage);
    };

    const getAgentStatus = (agentId) => {
        const agentStages = agents.find(a => a.id === agentId)?.stages || [];
        const stageStatuses = agentStages.map(stage => pipelineStatus[stage]?.status);

        if (stageStatuses.includes('active')) return 'active';
        if (stageStatuses.every(s => s === 'complete')) return 'complete';
        return 'idle';
    };

    const filterChatByAgent = (history, agentId) => {
        return history.filter(msg => !msg.agent || msg.agent === agentId);
    };

    const currentAgent = agents.find(a => a.id === selectedAgent);
    const filteredChat = filterChatByAgent(chatHistory, selectedAgent);

    return (
        <div className={`flex flex-col bg-white border-l-2 border-gray-200 transition-all ${isExpanded ? 'w-96' : 'w-16'}`}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
                {isExpanded ? (
                    <>
                        <div>
                            <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-2xl">{currentAgent?.emoji}</span>
                                <div>
                                    <div className="text-sm">{currentAgent?.name}</div>
                                    <div className="text-xs text-gray-500 font-normal">{currentAgent?.description}</div>
                                </div>
                            </h3>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setIsExpanded(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                            <button
                                onClick={onCloseSidebar}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </>
                ) : (
                    <button
                        onClick={() => setIsExpanded(true)}
                        className="text-gray-500 hover:text-gray-700 mx-auto"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                )}
            </div>

            {isExpanded && (
                <>
                    {/* Agent Selector */}
                    <div className="p-3 border-b border-gray-200 bg-gray-50">
                        <div className="text-xs font-semibold text-gray-600 mb-2 uppercase">AI Agents</div>
                        <div className="grid grid-cols-3 gap-2">
                            {agents.map(agent => {
                                const status = getAgentStatus(agent.id);
                                const isActive = isAgentActive(agent);
                                const isSelected = selectedAgent === agent.id;

                                return (
                                    <button
                                        key={agent.id}
                                        onClick={() => {
                                            setSelectedAgent(agent.id);
                                            onAgentChange(agent.id);
                                        }}
                                        className={`relative flex flex-col items-center p-2 rounded-lg border-2 transition-all ${
                                            isSelected
                                                ? getAgentColorClass(agent.color)
                                                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        } ${!isActive && 'opacity-50'}`}
                                        title={agent.description}
                                    >
                                        {/* Status indicator */}
                                        <div className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
                                            status === 'active' ? 'bg-green-500 animate-pulse' :
                                            status === 'complete' ? 'bg-blue-500' :
                                            'bg-gray-300'
                                        }`} />

                                        <span className="text-2xl mb-1">{agent.emoji}</span>
                                        <span className="text-xs font-medium text-center leading-tight">
                                            {agent.name.split(' ')[0]}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Chat Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {filteredChat.length === 0 ? (
                            <div className="text-center py-8">
                                <div className="text-6xl mb-4">{currentAgent?.emoji}</div>
                                <p className="text-gray-500 mb-2 font-medium">{currentAgent?.name}</p>
                                <p className="text-sm text-gray-400 mb-4">{currentAgent?.description}</p>

                                {/* Quick prompts based on agent */}
                                <div className="space-y-2">
                                    <p className="text-xs text-gray-400 font-semibold">Try asking:</p>
                                    {getQuickPromptsForAgent(currentAgent?.id).map((prompt, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setMessage(prompt)}
                                            className="block w-full text-left text-xs p-2 bg-blue-50 hover:bg-blue-100 rounded text-blue-700 transition-colors"
                                        >
                                            {prompt}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <>
                                {filteredChat.map((msg, idx) => (
                                    <div
                                        key={idx}
                                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div className={`max-w-[85%] rounded-lg px-3 py-2 ${
                                            msg.role === 'user'
                                                ? 'bg-blue-600 text-white'
                                                : `${getAgentBgClass(currentAgent?.color)} text-gray-800`
                                        }`}>
                                            {msg.role === 'assistant' && (
                                                <div className="text-xs font-semibold mb-1 opacity-75">
                                                    {currentAgent?.emoji} {currentAgent?.name}
                                                </div>
                                            )}
                                            <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                                        </div>
                                    </div>
                                ))}

                                {/* Typing indicator */}
                                {isTyping && (
                                    <div className="flex justify-start">
                                        <div className={`${getAgentBgClass(currentAgent?.color)} rounded-lg px-4 py-3`}>
                                            <div className="flex items-center gap-2">
                                                <div className="flex gap-1">
                                                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                                                </div>
                                                <span className="text-xs text-gray-600">{currentAgent?.name} is thinking...</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div ref={messagesEndRef} />
                            </>
                        )}
                    </div>

                    {/* Input Area */}
                    <div className="border-t border-gray-200 p-3 bg-gray-50">
                        <div className="flex gap-2 mb-2">
                            <textarea
                                ref={textareaRef}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={`Ask ${currentAgent?.name}...`}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                                rows="3"
                                disabled={isProcessing}
                            />
                        </div>
                        <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-500">
                                Press Enter to send
                            </p>
                            <button
                                onClick={handleSend}
                                disabled={!message.trim() || isProcessing}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                            >
                                Send
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function getQuickPromptsForAgent(agentId) {
    const prompts = {
        scout: [
            "Find datasets for sentiment analysis",
            "Search for image classification datasets",
            "Recommend datasets for time series"
        ],
        engineer: [
            "Clean this dataset for missing values",
            "Tokenize the text data",
            "Split into train/val/test sets"
        ],
        ml_engineer: [
            "Train a BERT model on this data",
            "Tune hyperparameters for better accuracy",
            "Set up cross-validation"
        ],
        evaluator: [
            "Calculate classification metrics",
            "Create confusion matrix",
            "Explain why validation loss is higher"
        ],
        mlops: [
            "Deploy this model to HuggingFace",
            "Create a REST API for the model",
            "Generate a Gradio demo"
        ],
        mentor: [
            "Explain how transformers work",
            "What's the difference between BERT and DistilBERT?",
            "How do I fix overfitting?"
        ]
    };
    return prompts[agentId] || [];
}

function getAgentBgClass(color) {
    const colors = {
        cyan: 'bg-cyan-100',
        green: 'bg-green-100',
        orange: 'bg-orange-100',
        purple: 'bg-purple-100',
        red: 'bg-red-100',
        blue: 'bg-blue-100'
    };
    return colors[color] || colors.blue;
}

export default AgentSidebar;
